/**
 * Die Wiedergabe-Pille unten: ihr Gerüst. (17.10.2026, aus app.ts `ensureVoicePrepEl` gezogen)
 *
 * Zwei Knöpfe in EINER Pille — kein neuer Knopf in der Fußleiste, keiner an anderer Stelle:
 *   - `.voice-prep-haupt`  „Unterbrechen – ich spreche“: stoppt UND öffnet das Mikro (Ticket 2695, lib/unterbrechen.ts);
 *                          in der Vorbereitung nur stoppen, im Zustand „Tippen zum Weiterspielen“ nichts (die Berührung
 *                          selbst setzt fort).
 *   - `.voice-prep-fertig` „Fertig“ (Martin, 17.10. 09:45): die Antwort ist abgeschlossen — alles stoppt, das Mikro
 *                          bleibt zu (lib/fertig.ts). Sichtbar, solange etwas vorbereitet wird, spricht oder pausiert.
 *
 * ⚠️ Echte `<button>`-Elemente (vorher war die ganze Pille ein `div role=button`): ein Knopf in einem Knopf ist für
 *    Bildschirmleser und Tastatur nicht bedienbar. Die Pille selbst ist jetzt eine Gruppe.
 * ⚠️ Kein Aussehen hier — das steht in frontend/style.css („Wiedergabe-Pille“).
 */
export interface VorlesePille {
  pille: HTMLElement;
  haupt: HTMLButtonElement;
  spin: HTMLElement;
  text: HTMLElement;
  fertig: HTMLButtonElement;
}

/** Was der Fertig-Knopf sagt — sichtbar „Fertig“, vorgelesen mit dem, was er tut. */
export const FERTIG_TEXT = "Fertig";
export const FERTIG_ERKLAERUNG = "Fertig – Vorlesen beenden, Mikro bleibt zu";

export function baueVorlesePille(an: {
  /** Tipp/Klick/Enter auf den Hauptteil; `taste`: über die Tastatur. */
  haupt: (taste: boolean) => void;
  fertig: (taste: boolean) => void;
}): VorlesePille {
  const pille = document.createElement("div");
  pille.className = "voice-prep-pill";
  pille.setAttribute("role", "group");
  pille.setAttribute("aria-label", "Vorlesen");

  const haupt = document.createElement("button");
  haupt.type = "button";
  haupt.className = "voice-prep-haupt";
  haupt.title = "Vorlesen stoppen";
  const spin = document.createElement("span");
  spin.className = "voice-prep-spin";
  spin.setAttribute("aria-hidden", "true");
  const text = document.createElement("span");
  text.className = "voice-prep-text";
  text.textContent = "Stimme wird vorbereitet …";
  haupt.append(spin, text);

  const fertig = document.createElement("button");
  fertig.type = "button";
  fertig.className = "voice-prep-fertig";
  fertig.title = FERTIG_ERKLAERUNG;
  fertig.setAttribute("aria-label", FERTIG_ERKLAERUNG);
  const haken = document.createElement("span");
  haken.className = "voice-prep-fertig-haken";
  haken.setAttribute("aria-hidden", "true");
  const wort = document.createElement("span");
  wort.textContent = FERTIG_TEXT;
  fertig.append(haken, wort);
  fertig.hidden = true;

  /*
   * `detail === 0`: der Klick kam über die Tastatur (Enter/Leertaste am Knopf) — bleibt im Stoppgrund unterscheidbar
   * (`pille-taste`, `pille-fertig-taste`), wie vorher über den eigenen keydown-Hörer.
   */
  haupt.addEventListener("click", (e) => an.haupt(e.detail === 0));
  fertig.addEventListener("click", (e) => {
    // Der Tipp auf „Fertig“ ist nicht zugleich ein Tipp auf die Pille.
    e.stopPropagation();
    an.fertig(e.detail === 0);
  });

  pille.append(haupt, fertig);
  // 21.10.2026: die gemessene Höhe als `--pille-h` — der Pfeil im Chat steigt um genau so viel über sie (style.css). Bricht
  // „Unterbrechen – ich spreche“ am schmalen Telefon um, ist sie höher als die 46 der Vorgabe.
  try {
    new ResizeObserver(() => {
      document.documentElement.style.setProperty("--pille-h", `${Math.ceil(pille.getBoundingClientRect().height)}px`);
    }).observe(pille);
  } catch {
    /* ohne ResizeObserver gilt die Vorgabe (46) */
  }
  // Unsichtbar bis zum ersten Zeichnen — und dabei nicht per Tastatur erreichbar.
  pille.inert = true;
  return { pille, haupt, spin, text, fertig };
}

export type PilleSichtbar = "prep" | "playing" | "paused" | "wartet";

/** Zeigt die Pille „Fertig“ in diesem Zustand? Vorbereitung, Sprechen, Pause — nicht beim Weiterspielen-Hinweis. */
export function fertigSichtbar(zustand: PilleSichtbar): boolean {
  return zustand !== "wartet";
}

/**
 * Die Pille in einem sichtbaren Zustand zeichnen (app.ts `zeigeVoicePrep` wählt den Text).
 *  - prep     Spinner, „Stimme wird vorbereitet …“ (oder worauf gewartet wird) — Hauptteil stoppt nur; „Fertig“ da
 *  - playing  Aufnahmepunkt, „Unterbrechen – ich spreche“ (Ticket 2695) — Text = Name des Hauptknopfs; „Fertig“ da
 *  - paused   wie playing
 *  - wartet   Weiter-Zeichen, „Tippen zum Weiterspielen“; kein „Fertig“
 */
export function zeichneVorlesePille(p: VorlesePille, zustand: PilleSichtbar, text: string): void {
  const aktiv = zustand === "playing" || zustand === "paused";
  const wartet = zustand === "wartet";
  p.spin.classList.toggle("is-stop", aktiv);
  p.spin.classList.toggle("is-weiter", wartet);
  p.pille.classList.toggle("is-playing", aktiv);
  p.pille.classList.toggle("is-wartet", wartet);
  p.pille.classList.toggle("is-unterbrechen", aktiv);
  p.text.textContent = text;
  if (aktiv) {
    p.haupt.title = text;
    p.haupt.setAttribute("aria-label", text);
  } else {
    p.haupt.title = wartet ? "Weiterspielen" : "Vorlesen stoppen";
    p.haupt.removeAttribute("aria-label");
  }
  const mitFertig = fertigSichtbar(zustand);
  p.fertig.hidden = !mitFertig;
  p.pille.classList.toggle("hat-fertig", mitFertig);
  p.pille.classList.add("is-visible");
  p.pille.inert = false;
}

/** Die Pille verbergen — unsichtbar heißt auch: nicht per Tastatur erreichbar (`inert`). */
export function verbergeVorlesePille(p: VorlesePille): void {
  p.pille.classList.remove("is-visible");
  p.pille.inert = true;
}
